import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { GlobalSearch } from './GlobalSearch';
import { NotificationBell } from './notifications/NotificationBell';
import { Lead } from '../types/lead';

interface EnhancedHeaderProps {
  onLeadSelect: (lead: Lead) => void;
  currentUser?: {
    name?: string;
    role?: string;
    avatar?: string;
  };
  onUserMenuClick?: () => void;
  onDarkModeToggle?: () => void;
  isDarkMode?: boolean;
}

export const EnhancedHeader: React.FC<EnhancedHeaderProps> = ({
  onLeadSelect,
  currentUser,
  onUserMenuClick,
  onDarkModeToggle,
  isDarkMode = false
}) => {
  const [isSearchFocused, setIsSearchFocused] = useState(false);

  return (
    <motion.header
      initial={{ opacity: 0, y: -20 }}
      animate={{ opacity: 1, y: 0 }}
      className="sticky top-0 z-50 bg-white/95 backdrop-blur-sm border-b border-gray-200 shadow-sm"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <motion.div
            whileHover={{ scale: 1.05 }}
            className="flex items-center space-x-3"
          >
            <div className="w-8 h-8 bg-gradient-to-br from-blue-500 to-purple-600 rounded-lg flex items-center justify-center">
              <span className="text-white font-bold text-sm">W</span>
            </div>
            <div>
              <h1 className="text-xl font-bold text-gray-900">WTF Amplify</h1>
              <p className="text-xs text-gray-500">CRM Dashboard</p>
            </div>
          </motion.div>

          {/* Global Search */}
          <div className="flex-1 max-w-2xl mx-8">
            <GlobalSearch
              onLeadSelect={onLeadSelect}
              className={`transition-all duration-300 ${
                isSearchFocused ? 'scale-105' : ''
              }`}
            />
          </div>

          {/* Right Side Controls */}
          <div className="flex items-center space-x-4">
            {/* Dark Mode Toggle */}
            {onDarkModeToggle && (
              <motion.button
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.9 }}
                onClick={onDarkModeToggle}
                className="p-2 rounded-lg bg-gray-100 hover:bg-gray-200 transition-colors duration-200"
              >
                <span className="text-lg">
                  {isDarkMode ? '☀️' : '🌙'}
                </span>
              </motion.button>
            )}

            {/* Notifications */}
            <NotificationBell />

            {/* User Menu */}
            {currentUser && (
              <motion.button
                whileHover={{ scale: 1.05 }}
                onClick={onUserMenuClick}
                className="flex items-center space-x-3 p-2 rounded-lg hover:bg-gray-100 transition-colors duration-200"
              >
                <div className="w-8 h-8 bg-gradient-to-br from-blue-500 to-purple-600 rounded-full flex items-center justify-center">
                  <span className="text-white font-medium text-sm">
                    {currentUser.name?.charAt(0)?.toUpperCase() || 'U'}
                  </span>
                </div>
                <div className="hidden md:block text-left">
                  <p className="text-sm font-medium text-gray-900">{currentUser.name || 'User'}</p>
                  <p className="text-xs text-gray-500">{currentUser.role || 'Unknown'}</p>
                </div>
              </motion.button>
            )}
          </div>
        </div>
      </div>
    </motion.header>
  );
}; 